class RelatedProductsSnippet extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.cartUpdateUnsubscriber = undefined;
  }

  connectedCallback() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!this.isConnected || !this.closest('cart-drawer') || !event.cartData?.items) return;

        const cartItems = this.normalizeCartItems(event.cartData.items);
        this.dataset.cartItems = JSON.stringify(cartItems);
        this.syncRecommendationsFromCart(cartItems);
      });
    }

    this.syncRecommendationsFromCart(this.parseCartItems());
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = undefined;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  parseCartItems() {
    const raw = this.dataset.cartItems || '[]';

    try {
      return this.normalizeCartItems(JSON.parse(raw));
    } catch (e) {
      try {
        return this.normalizeCartItems(JSON.parse(this.decodeHtml(raw)));
      } catch (e2) {
        console.warn('Failed to parse cart items:', e2, 'cartItemsString:', raw);
        return [];
      }
    }
  }

  normalizeCartItems(items) {
    return items.map((item) => ({
      ...item,
      product_id: item.product_id,
      product_title: item.product_title || item.title || '',
    }));
  }

  syncRecommendationsFromCart(cartItems) {
    // IMPORTANT: Shopify adds new products at the BEGINNING of the array (index 0), not the end
    const currentProductId = cartItems.length > 0 ? cartItems[0].product_id : null;
    const currentCartCount = cartItems.length;

    const storageKey = 'cart-recommendations-state';
    const stored = sessionStorage.getItem(storageKey);
    let lastProductId = null;

    if (stored) {
      try {
        lastProductId = JSON.parse(stored).productId;
      } catch (e) {
        console.warn('Failed to parse stored state:', e);
      }
    }

    // Only reload if the base product changed, not just the quantity
    if (lastProductId === currentProductId && lastProductId !== null) {
      sessionStorage.setItem(storageKey, JSON.stringify({
        productId: currentProductId,
        cartCount: currentCartCount,
      }));

      const container = this.querySelector('[data-recommendations-container]');
      const slides = container ? container.querySelectorAll('.swiper-slide') : [];

      if (!this.swiper && slides.length > 0) {
        setTimeout(() => this.initializeSwiper(), 100);
      } else if (slides.length === 0) {
        setTimeout(() => {
          if (this.dataset.automatic === 'true') {
            this.loadAutomaticRecommendations();
          }
        }, 100);
      }
      return;
    }

    sessionStorage.setItem(storageKey, JSON.stringify({
      productId: currentProductId,
      cartCount: currentCartCount,
    }));

    if (lastProductId !== null && lastProductId !== currentProductId) {
      sessionStorage.removeItem('last-recommended-ids');

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      const container = this.querySelector('[data-recommendations-container]');
      this.showRecommendationSkeletons(container);
    }

    setTimeout(() => {
      if (this.dataset.automatic === 'true') {
        this.loadAutomaticRecommendations();
      } else {
        this.initializeSwiper();
      }
    }, 100);
  }

  async loadAutomaticRecommendations() {
    const cartItems = this.parseCartItems();
    const container = this.querySelector('[data-recommendations-container]');
    
    if (!cartItems.length || !container) {
      // Clear stored state when cart is empty
      sessionStorage.removeItem('cart-recommendations-state');
      sessionStorage.removeItem('last-recommended-ids');
      this.showEmptyState(container);
      return;
    }

    if (container.querySelector('.swiper-slide')) {
      this.showRecommendationSkeletons(container);
    }

    // Find the most recently added product (FIRST in array, as Shopify adds new items at index 0)
    const currentProduct = cartItems[0];
    const lastProductId = currentProduct.product_id;
    const productTitle = currentProduct.product_title || currentProduct.title || '';

    try {
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      
      // Try with 'complementary' intent first, fallback to 'related'
      let url = `/recommendations/products.json?product_id=${lastProductId}&limit=8&intent=complementary&_=${timestamp}`;
      
      let response = await fetch(url, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      let data = await response.json();
      
      // If no complementary products or same as before, try related
      if (!data.products || data.products.length === 0) {
        url = `/recommendations/products.json?product_id=${lastProductId}&limit=8&intent=related&_=${timestamp}`;
        response = await fetch(url, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        data = await response.json();
      }

      if (data.products && data.products.length > 0) {
        // Log the product IDs to verify they're different
        const currentProductIds = [];
        data.products.forEach((product, idx) => {
          currentProductIds.push(product.id);
        });
        
        // Check if these are the same products as last time (API might be returning generic recommendations)
        const lastRecommendedIds = sessionStorage.getItem('last-recommended-ids');
        const isSameProducts = lastRecommendedIds === JSON.stringify(currentProductIds);
        
        if (isSameProducts && lastRecommendedIds) {
          // Extract key words from product title for search
          const searchKeywords = this.extractSearchKeywords(productTitle);
          
          const searchProducts = await this.fetchSearchRecommendations(searchKeywords, 8);
          
          if (searchProducts && searchProducts.length > 0) {
            // Filter out products that are already in the API recommendations
            const filteredProducts = searchProducts.filter(searchProd => {
              return !currentProductIds.includes(searchProd.id);
            });
            
            if (filteredProducts.length > 0) {
              data.products = filteredProducts;
              // Update IDs with the search results
              currentProductIds.length = 0;
              filteredProducts.forEach(product => {
                currentProductIds.push(product.id);
              });
            }
          }
        }
        
        // Store FINAL product IDs (after potential fallback) for next comparison
        sessionStorage.setItem('last-recommended-ids', JSON.stringify(currentProductIds));
        
        // Use Section Rendering API to render products with Liquid templates
        await this.renderProductsWithSection(data.products, container);

        // Wait a bit for DOM to be ready
        setTimeout(() => this.initializeSwiper(), 50);
      } else {
        console.warn('⚠️ No products in response');
        this.showEmptyState(container, 'No recommendations available');
      }
    } catch (error) {
      console.error('Error loading recommendations:', error);
      this.showEmptyState(container, 'Error loading recommendations');
    }
  }

  async renderProductsWithSection(products, container) {
    // Fetch all products in parallel for better performance
    const fetchPromises = products
      .filter(product => product.handle)
      .map(async (product) => {
        try {
          const response = await fetch(
            `/products/${product.handle}?view=cart-drawer-recommendation&_=${Date.now()}`
          );
          if (!response.ok) return null;
          
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          return doc.querySelector('.swiper-slide');
        } catch (error) {
          console.error(`Error rendering product ${product.handle}:`, error);
          return null;
        }
      });
    
    const slides = await Promise.all(fetchPromises);

    // Swap content only once slides are ready — keeps skeleton/existing cards visible during fetch
    container.replaceChildren();

    // Append slides in order, skipping nulls
    slides.forEach(slide => {
      if (slide) {
        const importedSlide = document.importNode(slide, true);
        container.appendChild(importedSlide);
      }
    });
  }

  async fetchSearchRecommendations(searchQuery, limit = 8) {
    try {
      // Use Shopify's Predictive Search API as fallback
      const url = `/search/suggest.json?q=${encodeURIComponent(searchQuery)}&resources[type]=product&resources[limit]=${limit * 2}`;
      
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Extract products from response
      let products = [];

      if (data.resources?.results?.products) {
        products = data.resources.results.products;
      } else if (data.products) {
        products = data.products;
      }

      // Shuffle and return random products
      if (products.length > 0) {
        const shuffled = products.sort(() => 0.5 - Math.random());
        return shuffled.slice(0, limit);
      }

      return [];
    } catch (error) {
      console.error('Error fetching search recommendations:', error);
      return [];
    }
  }

  extractSearchKeywords(title) {
    // Remove common words and extract meaningful keywords
    const stopWords = ['the', 'for', 'and', 'with', 'includes', 'include', 'box', 'of', 'a', 'an'];
    const words = title.toLowerCase().split(/[\s\-()]+/);
    
    // Get first 2-3 meaningful words
    const keywords = words
      .filter(word => word.length > 2 && !stopWords.includes(word))
      .slice(0, 2)
      .join(' ');
    
    return keywords || words[0] || title;
  }

  showRecommendationSkeletons(container) {
    if (!container) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'recommendation-skeletons';
    wrapper.setAttribute('aria-hidden', 'true');

    for (let i = 0; i < 2; i += 1) {
      const skeleton = document.createElement('div');
      skeleton.className = 'recommendation-skeleton';

      const image = document.createElement('div');
      image.className = 'recommendation-skeleton__image';

      const line = document.createElement('div');
      line.className = 'recommendation-skeleton__line';

      const shortLine = document.createElement('div');
      shortLine.className = 'recommendation-skeleton__line recommendation-skeleton__line--short';

      skeleton.append(image, line, shortLine);
      wrapper.appendChild(skeleton);
    }

    container.replaceChildren(wrapper);

    const prevBtn = this.querySelector('.swiper-button-prev');
    const nextBtn = this.querySelector('.swiper-button-next');
    prevBtn?.classList.add('is-hidden');
    nextBtn?.classList.add('is-hidden');
  }

  showEmptyState(container, message = 'No products available') {
    if (container) {
      // Create element safely without innerHTML
      const wrapper = document.createElement('div');
      wrapper.style.textAlign = 'center';
      wrapper.style.padding = '2rem';
      
      const text = document.createElement('p');
      text.textContent = message;
      
      wrapper.appendChild(text);
      container.replaceChildren(); // Clear first
      container.appendChild(wrapper);
    }
  }

  initializeSwiper() {
    const swiperElement = this.querySelector('.swiper-related-products-snippet');
    const prevBtn = this.querySelector('.swiper-button-prev');
    const nextBtn = this.querySelector('.swiper-button-next');
    const slides = this.querySelectorAll('.swiper-slide');

    if (!prevBtn || !nextBtn) {
      return;
    }

    prevBtn.classList.add("is-hidden");
    nextBtn.classList.add("is-hidden");

    if (!swiperElement || typeof Swiper === "undefined") {
      return;
    }

    // Destroy previous instance if it exists
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.swiper = new Swiper(swiperElement, {
      slidesPerView: "auto",
      spaceBetween: 16,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
      on: {
        init: (swiper) => {
          this.updateNavigationButtons(swiper, prevBtn, nextBtn);
        },

        slideChange: (swiper) => {
          this.updateNavigationButtons(swiper, prevBtn, nextBtn);
        },

        reachBeginning: () => {
          prevBtn.classList.add("is-hidden");
          nextBtn.classList.remove("is-hidden");
        },

        reachEnd: () => {
          nextBtn.classList.add("is-hidden");
          prevBtn.classList.remove("is-hidden");
        },

        fromEdge: () => {
          prevBtn.classList.remove("is-hidden");
          nextBtn.classList.remove("is-hidden");
        }
      },
      observer: true,
      observeParents: true
    });
  }

  updateNavigationButtons(swiper, prevBtn, nextBtn) {
    if (swiper.isBeginning) {
      prevBtn.classList.add("is-hidden");
      nextBtn.classList.remove("is-hidden");
    } else if (swiper.isEnd) {
      nextBtn.classList.add("is-hidden");
      prevBtn.classList.remove("is-hidden");
    } else {
      nextBtn.classList.remove("is-hidden");
      prevBtn.classList.remove("is-hidden");
    }
  }

  decodeHtml(html) {
    // Use DOMParser instead of innerHTML for security
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<!doctype html><body>${html}`, 'text/html');
    return doc.body.textContent || '';
  }
}

if (!customElements.get("related-products-snippet")) {
  customElements.define("related-products-snippet", RelatedProductsSnippet);
}

export default RelatedProductsSnippet;
